import axios, { AxiosResponse, CancelTokenSource } from "axios";

import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { getPopularDesstinations, searchCities } from "api/home";
import { debounce } from "src/utils/debounce";
import { PopularCities } from "constants/popularCities";
import {
  isEmpty,
  isWindowPresent,
  updateRecentSearch,
} from "src/utils/browserHelpers";
import dayjs from "dayjs";
import { useRouter } from "next/navigation";
import { City, CityListState, CityType } from "src/containers/home/types";
import CityList from "src/containers/home/cityList";
import cloneDeep from "lodash.clonedeep";
import { ResultsContext } from "src/context/resultsContext/context";

const useBusSearch = (component = "home") => {
  const router = useRouter();
  const cancelTokenSource = useRef<CancelTokenSource | null>(null);

  const [searchValue, setSearchValue] = useState({
    departure: "",
    arrival: "",
  });
  const { setResultsLoading } = useContext(ResultsContext);
  const [openDropDownList, setOpenDropDownList] = useState({
    arrival: false,
    departure: false,
  });
  const [calendarValue, setCalendarValue] = useState({
    open: false,
    selectedDate: dayjs().toDate(),
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({ arrival: false, departure: false });
  const [selectedCity, setSelectedCity] = useState<{
    arrival: City | null;
    departure: City | null;
  }>({
    arrival: null,
    departure: null,
  });
  const [noData, setNoData] = useState({ arrival: false, departure: false });
  const [citesList, setCitiesList] = useState<CityListState>({
    departure: {
      suggestions: [],
      default: PopularCities,
    },
    arrival: {
      popular: [],
      nearBy: [],
      default: PopularCities,
      suggestions: [],
    },
  });

  const [activeList, setActiveList] = useState<CityType | "">("");

  const handleSearchValue = (value: string) => {
    if (!value.length) {
      if (noData) setNoData({ ...noData, [activeList as CityType]: false });

      setCitiesList({
        ...citesList,
        [activeList]: { ...citesList[activeList as CityType], suggestions: [] },
        ...(activeList === "departure" && {
          arrival: { ...citesList.arrival, popular: [], nearBy: [] },
        }),
      });
      setSelectedCity({
        ...selectedCity,
        [activeList]: null,
      });

      if (component === "editSearch") {
        setError({ ...error, [activeList]: true });
      }
      if (cancelTokenSource.current) {
        cancelTokenSource.current.cancel("Request canceled");
      }
    } else setError({ ...error, [activeList]: false });
    if (value.length < 3) {
      setCitiesList({
        ...citesList,
        [activeList]: {
          ...citesList[activeList as CityType],
          suggestions: [],
        },
      });
    }
    setSearchValue({
      ...searchValue,
      [activeList]: value,
    });
  };
  const handleOnChange = debounce(
    useCallback(
      async (value: string, type: string) => {
        if (noData[type as CityType]) setNoData({ ...noData, [type]: false });
        if (value.length >= 3) {
          setLoading(true);
          if (cancelTokenSource.current) {
            cancelTokenSource.current.cancel("Request canceled");
          }

          // Create a new cancel token source for the current request
          cancelTokenSource.current = axios.CancelToken.source();
          try {
            const response = await searchCities(value, {
              cancelToken: cancelTokenSource.current.token,
            });
            const { data } = response as AxiosResponse;

            if (data?.success) {
              if (noData[type as CityType])
                setNoData({ ...noData, [type]: false });
              if (data.data?.suggestions?.length) {
                setCitiesList({
                  ...citesList,
                  [type]: {
                    ...citesList[type as CityType],
                    suggestions: data.data?.suggestions,
                  },
                });
              } else {
                setNoData({ ...noData, [type]: true });
                setCitiesList({
                  ...citesList,
                  [type]: {
                    ...citesList[type as CityType],
                    suggestions: [],
                  },
                });
              }
            } else {
              if (axios.isCancel(response)) {
                return;
              }
            }
          } catch (e) {
            setCitiesList({
              ...citesList,
              [type]: {
                ...citesList[type as CityType],
                suggestions: [],
              },
            });
            console.error(e);
          } finally {
            setLoading(false);
          }
        } else if (value.length > 0 && value.length < 3) {
          setCitiesList({
            ...citesList,
            [type]: {
              ...citesList[type as CityType],
              suggestions: [],
            },
          });
        }
      },
      [citesList, noData]
    ),
    300
  );
  const handlePopularDestinations = useCallback(
    async (cityId: number, cityName: string) => {
      try {
        setLoading(true);
        const resp = (await getPopularDesstinations(
          cityId,
          cityName
        )) as AxiosResponse;
        if (resp.data?.success) {
          setCitiesList({
            ...citesList,
            ["arrival"]: {
              ...citesList.arrival,
              popular: resp.data?.data?.popularCities,
              nearBy: resp.data?.data?.nearByCities,
            },
          });
        } else {
        }
      } catch (e) {
        console.error(e, "error");
      } finally {
        setLoading(false);
      }
    },
    [citesList]
  );

  const handleSelectCity = useCallback(
    (city: City) => {
      setSelectedCity({
        ...selectedCity,
        [activeList]: city,
      });
      setSearchValue({
        ...searchValue,
        [activeList]: city.cityName,
      });
      setOpenDropDownList({ arrival: false, departure: false });
      if (error.arrival && error.departure)
        setError({ arrival: false, departure: false });
      else if (error[activeList as CityType]) {
        setError({ ...error, [activeList]: false });
      }
      if (activeList === "departure") {
        handlePopularDestinations(city.cityId, city.cityName);
      }
      handleOverlay(false);
    },
    [activeList, error, handlePopularDestinations, searchValue, selectedCity]
  );

  const handleSwap = useCallback(() => {
    if (selectedCity?.arrival && selectedCity?.departure) {
      const swapCities = cloneDeep(selectedCity);
      const arrivalCity = { ...swapCities.arrival };
      swapCities.arrival = swapCities.departure;
      swapCities.departure = arrivalCity as City;
      handlePopularDestinations(
        arrivalCity?.cityId as number,
        arrivalCity?.cityName as string
      );
      setSearchValue({
        arrival: swapCities.arrival.cityName,
        departure: swapCities.departure.cityName,
      });
      setSelectedCity(swapCities);
    }
  }, [handlePopularDestinations, selectedCity]);

  const CityListComp = useMemo(() => {
    return (
      <CityList
        cities={citesList}
        cityListActive={activeList as CityType}
        handleSelectCity={handleSelectCity}
        loading={loading}
        noData={noData}
        onClose={() =>
          setOpenDropDownList({ arrival: false, departure: false })
        }
      />
    );
  }, [activeList, citesList, handleSelectCity, loading, noData]);

  const isAllValid = useCallback(() => {
    if (
      (isEmpty(selectedCity.arrival) && isEmpty(selectedCity.departure)) ||
      (!searchValue.arrival && !searchValue.departure) ||
      (searchValue.arrival &&
        searchValue.departure &&
        noData.arrival &&
        noData.departure)
    ) {
      return { arrival: true, departure: true };
    } else if (
      isEmpty(selectedCity.arrival) ||
      !searchValue.arrival ||
      (searchValue.arrival && noData.arrival)
    ) {
      return { arrival: true };
    } else if (
      isEmpty(selectedCity.departure) ||
      !searchValue.departure ||
      (searchValue.departure && noData.departure)
    ) {
      return { departure: true };
    } else if (
      selectedCity?.arrival?.cityId === selectedCity?.departure?.cityId
    ) {
      return { arrival: true, departure: true };
    }

    return true;
  }, [
    selectedCity.arrival,
    selectedCity.departure,
    searchValue.arrival,
    searchValue.departure,
    noData.arrival,
    noData.departure,
  ]);

  const handleSearch = () => {
    const allValid = isAllValid();
    if (allValid !== true) {
      setError({ ...error, ...allValid });
      return;
    } else {
      if (component === "editSearch") {
        setResultsLoading(true);
      }
      const { arrival, departure } = selectedCity;
      const journeyDate = dayjs(calendarValue.selectedDate).format(
        "YYYY-MM-DD"
      );
      if (arrival && departure)
        updateRecentSearch({
          arrival,
          departure,
          journeyDate,
        });

      if (isWindowPresent) {
        sessionStorage.removeItem("selectedFilters");
        sessionStorage.removeItem("filterList");
      }

      const splitDepartureCity = departure?.cityName.split(",")[0];
      const splitArrivalCity = arrival?.cityName.split(",")[0];

      if (departure && arrival) {
        const queryString = `fromCity=${departure.cityId}&toCity=${arrival.cityId}&journeyDate=${journeyDate}&fromCityName=${splitDepartureCity}&toCityName=${splitArrivalCity}`;
        router.refresh();
        router.push(`/bus/results?${queryString}`);
      }
    }
  };
  const handleOverlay = (apply: boolean) => {
    const rootLayout = document.querySelector(".root-layout");
    const overlayDiv = document.querySelector(".overlay");

    if (apply && !overlayDiv) {
      const overlayDiv = document.createElement("div");

      overlayDiv.className = "overlay";
      rootLayout?.appendChild(overlayDiv);
    } else if (!apply) {
      overlayDiv && rootLayout?.removeChild(overlayDiv);
    }
  };
  return {
    handleSearch,
    handleOnChange,
    searchValue,
    handleSearchValue,
    CityListComp,
    setActiveList,
    openDropDownList,
    setOpenDropDownList,
    error,
    setSearchValue,
    setCalendarValue,
    calendarValue,
    setSelectedCity,
    handleSwap,
    setCitiesList,
    activeList,
    handleOverlay,
    handlePopularDestinations,
    setNoData,
  };
};

export default useBusSearch;
